<template>
  <div class="content">
    <template v-if="applications?.length">
      <template v-for="application in applications" :key="application.id">
        <div class="table table--vertical table--lorem">
          <table class="table__inner">
            <colgroup>
              <col span="1" style="width: 38%;">
              <col span="1" style="width: 30%;">
              <col span="1" style="width: 32%;">
            </colgroup>
            <tbody>
              <tr class="table__tr">
                <td class="table__td" data-title="Заявление">
                  <router-link
                    :to="{
                      name: 'secretary.application',
                      params: { formId: application.form_id, applicationId: application.id, section: 'overview' },
                    }"
                    v-if="readonly"
                  >
                    №{{ application.sspvo_unique_code }} ({{ application.education_level }})
                  </router-link>
                  <router-link
                    :to="{
                      name: 'abiturient.application',
                      params: { id: application.id, section: 'overview' },
                    }"
                    v-else
                  >
                    №{{ application.sspvo_unique_code }} ({{ application.education_level }})
                  </router-link>
                </td>
                <td class="table__td" data-title="Статус">
                  <span class="join join--center">
                    Статус: {{ application.status_text }}
                    <span
                      v-if="application.email_matched === false"
                      v-tooltip="{ content: APPLICATION_EMAIL_NOT_MATCHED_TEXT, theme: 'tooltip-danger' }"
                    >
                      <img class="card__icon icon icon--danger" src="~images/info.svg" v-svg-inline/>
                    </span>
                  </span>
                </td>
                <td class="table__td" data-title="Действия">
                  <button
                    class="btn btn--primary"
                    :disabled="isImporting"
                    @click="startImport(application)"
                  >
                    <t-loader
                      v-if="isApplicationImporting(application)"
                      size="sm"
                      tone="inherit"
                      :text="progressMessage || 'Обновляем'"
                    />
                    <template v-else>Обновить</template>
                  </button>
                  <span>Обновлено: {{ application.updated_at }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="card card--danger card--danger-dark" v-if="application.email_matched === false">
          <img class="card__icon icon icon--danger" src="~images/info.svg" v-svg-inline/>
          <p>
            <strong>ВНИМАНИЕ!</strong> <span v-html="APPLICATION_EMAIL_NOT_MATCHED_TEXT_APPLICATION"></span>
          </p>
        </div>

        <div class="card card--info" v-if="application.fdo">
          <img class="card__icon icon icon--primary" src="~images/info.svg" v-svg-inline/>
          <p>
            Пожалуйста, ознакомьтесь с
            <strong>
              <a href="https://storage.tusur.ru/files/190468/2026-instruktsiya-vi-fdo.pdf" target="_blank">инструкцией</a>
            </strong>
            по сдаче вступительных испытаний
          </p>
        </div>

        <div class="table table--vertical table--lorem" v-if="application.entrance_exams?.length">
          <table class="table__inner">
            <colgroup>
              <col span="1" style="width: 19%;">
              <col span="1" style="width: 19%;">
              <col span="1" style="width: 30%;">
              <col span="1" style="width: 32%;">
            </colgroup>
            <thead class="table__thead">
              <tr class="table__tr">
                <th class="table__th">Предмет</th>
                <th class="table__th">Результат</th>
                <th class="table__th">Дата проведения (по Томскому времени)</th>
                <th class="table__th">Место проведения</th>
              </tr>
            </thead>
            <tbody>
              <tr class="table__tr" v-for="exam in application.entrance_exams" :key="exam.id">
                <td class="table__td" data-title="Предмет">{{ exam.discipline }}</td>
                <td class="table__td" data-title="Результат">{{ exam.result }}</td>
                <td class="table__td" data-title="Дата (по Томску)">{{ exam.date }}</td>
                <td class="table__td" data-title="Место">
                  <div v-if="exam.location.moodle && exam.location.redirectable">
                    <button
                      class="btn btn--primary"
                      :class="{ 'hint hint--top': exam.location.hint }"
                      :data-hint="exam.location.hint"
                      :disabled="readonly || !exam.location.active"
                      @click="redirectToMoodle(application.id, exam.id)"
                    >
                      {{ exam.location.message }}
                    </button>
                  </div>
                  <div v-else>{{ exam.location.message}}</div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-else>
          <div class="card card--info" v-if="application.education_level_slug === 'bachelor'">
            <img class="card__icon icon icon--primary" src="~images/info.svg" v-svg-inline/>
            <p>Вы не записаны на вступительные испытания</p>
          </div>
          <div class="card card--info" v-else>
            <img class="card__icon icon icon--primary" src="~images/info.svg" v-svg-inline/>
            <p>Информацию о вступительных испытаниях уточняйте в отборочной комиссии</p>
          </div>
        </div>
      </template>
    </template>

    <template v-else-if="readonly">
      <div class="card card--info">
        <img class="card__icon icon icon--primary" src="~images/info.svg" v-svg-inline/>
        <p>Экзамены не найдены</p>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { APPLICATION_EMAIL_NOT_MATCHED_TEXT } from '@shared/config'
import { TLoader } from '@shared/ui'
import { APPLICATION_EMAIL_NOT_MATCHED_TEXT_APPLICATION } from '@shared/config'
import { useFormApplicationImport } from '../../model/useFormApplicationImport'
import { api } from '@shared/api'
import { toast } from '@shared/lib'

const props = defineProps({
  applications: { type: Array, default: () => [] },
  readonly: { type: Boolean, default: false },
  formId: { type: [String, Number], default: null },
})
const emit = defineEmits(['refetch-form'])

const {
  progressMessage,
  isImporting,
  startImport,
  isApplicationImporting,
} = useFormApplicationImport({
  applications: computed(() => props.applications),
  formId: computed(() => props.formId),
  onFinished: () => emit('refetch-form'),
})

const redirectToMoodle = async (applicationId, examId) => {
  try {
    const { data } = await api.post(`/api/v1/abiturient/applications/${applicationId}/entrance_exams/${examId}/redirect_to_moodle`)
    window.location.href = data.redirect_url
  } catch {
    toast.error('Не удалось перейти: экзамен не доступен')
    emit('refetch-form')
  }
}
</script>
